// Region-aware pricing.
//
// Every priced entity in ui-metadata (variants, accessories, packages, colours)
// carries a `price` map keyed by the `country` URL parameter, with a `default`
// fallback — e.g. `{ KSA: 452400, UAE: 429100, …, default: 429100 }`. The
// matching `config.regions` entry says which currency those numbers are in and
// what the USD conversion factor is.
//
// Before this, every call site read `price.UAE` and printed a hardcoded "AED",
// so a `?country=KSA` visitor saw Dirham prices labelled as Dirhams. Resolve
// through here instead.

import { getCountryParameter } from './common';
import { uimetadata } from './metadata';

/** Local-currency price map as stored in ui-metadata. */
export type PriceMap = Record<string, number | undefined>;

export interface RegionConfig {
  /** Human-readable region name, as spelled in the GM pricing sheets. */
  label: string;
  /** ISO code rendered next to every amount (AED, SAR, KWD, …). */
  currency: string;
  /** Local-currency units per 1 USD — `local = usd * fx`. */
  fx: number;
}

const DEFAULT_REGION: RegionConfig = {
  label: 'UAE',
  currency: 'AED',
  fx: 3.6809475499749165,
};

const regionTable = (): Record<string, RegionConfig> =>
  ((uimetadata as { config?: { regions?: Record<string, RegionConfig> } })
    ?.config?.regions ?? {});

/**
 * Region key for this session: the `country` URL parameter when it names a
 * region we have pricing for, otherwise `'default'`. Country values that exist
 * for lead capture but not for pricing (e.g. Iraq) land on `'default'` rather
 * than dropping to a 0 price.
 */
export const getRegionKey = (): string => {
  const country = getCountryParameter();
  return country in regionTable() ? country : 'default';
};

/** Currency + FX for the active region, falling back to the UAE defaults. */
export const getRegion = (): RegionConfig =>
  regionTable()[getRegionKey()] ?? DEFAULT_REGION;

/** ISO currency code for the active region — 'AED', 'SAR', 'KWD', … */
export const getCurrency = (): string => getRegion().currency;

/**
 * Master toggle for per-item pricing: the price line on variant / colour /
 * accessory / package cards, the trim price in the MODEL list, the per-line
 * prices and total in the SUMMARY tab, and whether paint and interior trim
 * count toward the headline total.
 *
 * Defaults to OFF so a linkId that has not opted in behaves exactly as it did
 * before per-item pricing existed. This does NOT gate the header / mobile-footer
 * "Starting Price", which every project has always shown.
 */
export const arePricesVisible = (): boolean =>
  !arePricesHiddenForCountry() &&
  (uimetadata as { featureFlags?: { showPrices?: boolean } })?.featureFlags?.showPrices === true;

// Countries with no published pricing yet: every price is hidden on these
// links, for every model, the same way the Cadillac linkIds hide theirs.
// Empty now that IQD (and LBP) are priced in USD - add a country code back here
// to hide its prices again.
const NO_PRICE_COUNTRIES: string[] = [];

/** True on a link whose country is in NO_PRICE_COUNTRIES — no price anywhere in the UI. */
export const arePricesHiddenForCountry = (): boolean =>
  NO_PRICE_COUNTRIES.includes(getCountryParameter().toUpperCase());

/**
 * Whether the car price itself (header / mobile-footer "Starting Price … MSRP"
 * and the SUMMARY breakdown's trim line) is shown. On by default; a linkId opts
 * out with `featureFlags.showDisplayCarPrice: false` (the Cadillac linkIds,
 * which must show no MSRP anywhere — only dealer prices in Matching Vehicles),
 * and it is always off on a no-pricing country link (NO_PRICE_COUNTRIES).
 */
export const isCarPriceVisible = (): boolean =>
  !arePricesHiddenForCountry() &&
  (uimetadata as { featureFlags?: { showDisplayCarPrice?: boolean } })?.featureFlags?.showDisplayCarPrice !== false;

/**
 * Price for display: the resolved amount, or 0 when this project has per-item
 * pricing switched off — call sites already render nothing for 0.
 */
export const getDisplayPrice = (price?: PriceMap | null): number =>
  arePricesVisible() ? getPrice(price) : 0;

/**
 * Local-currency amount from a metadata `price` map.
 *
 * Two shapes exist in the repo and they need different fallbacks:
 *
 *  - **Region-aware** (model declares `config.regions`, e.g. Corvette 2387):
 *    `price[region] ?? price.default`. It must NOT fall back to `price.UAE` —
 *    that would drop an AED figure into a KWD or SAR total.
 *  - **Legacy** (no `regions` block — every other linkId): the call sites used
 *    to read `price.UAE ?? price.default` directly, and 51 entries across Tahoe,
 *    Optiq and Lyriq rely on it: they carry `{ UAE: 7889, default: 0 }`, so
 *    reading `default` first would silently price them at zero. Keep the old
 *    chain for those models.
 */
export const getPrice = (price?: PriceMap | null): number => {
  if (!price) return 0;
  const regionAware = Object.keys(regionTable()).length > 0;
  if (regionAware) return price[getRegionKey()] ?? price.default ?? 0;
  return price.UAE ?? price.default ?? 0;
};

/**
 * Convert a USD figure to the active region's currency. The colour, option,
 * accessory and package sheets are published in USD only; variant sheets quote
 * local currency directly and must NOT be run through this.
 *
 * Rounded to whole units to match how the MSRP sheet rounds its own local
 * prices — GCC currencies are not quoted with fils/halala at this price point.
 */
export const usdToLocal = (usd: number, region: RegionConfig = getRegion()): number =>
  Math.round(usd * region.fx);

/** `"AED 429,100"` — the standard price rendering across the configurator. */
export const formatPrice = (
  amount: number,
  opts: { currency?: string; sign?: boolean } = {},
): string => {
  const currency = opts.currency ?? getCurrency();
  const prefix = opts.sign && amount > 0 ? '+' : '';
  return `${prefix}${currency} ${amount.toLocaleString()}`;
};
